import { useEffect, useState } from 'react';
import api from '../lib/api';

/**
 * useOrgFeatures — דגלי הפיצ'רים של הארגון (Organization.features).
 *
 * הנתונים מגיעים מ-GET /auth/me שכבר מחזיר organization.features (תחת
 * impersonation — של העמותה המאומפרסנת). cache ברמת module כדי שכמה
 * קומפוננטות (Sidebar, עמודים) לא יפציצו את השרת; refreshOrgFeatures()
 * מרענן בכוח (למשל אחרי כניסת/יציאת impersonation).
 *
 * חשוב: זה גיטינג תצוגתי בלבד — האכיפה האמיתית היא requireFeature בשרת.
 */

export type OrgFeatures = Record<string, boolean>;

let cached: OrgFeatures | null = null;
let inflight: Promise<OrgFeatures> | null = null;
const listeners = new Set<(f: OrgFeatures) => void>();

async function fetchFeatures(): Promise<OrgFeatures> {
  try {
    const res = await api.get('/auth/me');
    const features: OrgFeatures = res.data?.organization?.features || {};
    cached = features;
    listeners.forEach((l) => l(features));
    return features;
  } catch {
    // כשל שקט — בלי דגלים (fail-closed בתצוגה; השרת אוכף ממילא)
    return cached || {};
  } finally {
    inflight = null;
  }
}

export function refreshOrgFeatures(): Promise<OrgFeatures> {
  inflight = fetchFeatures();
  return inflight;
}

export function useOrgFeatures(): { features: OrgFeatures; loaded: boolean } {
  const [features, setFeatures] = useState<OrgFeatures>(cached || {});
  const [loaded, setLoaded] = useState(cached !== null);

  useEffect(() => {
    const onUpdate = (f: OrgFeatures) => { setFeatures(f); setLoaded(true); };
    listeners.add(onUpdate);
    if (cached === null && !inflight) {
      inflight = fetchFeatures();
    }
    if (inflight) inflight.then(onUpdate).catch(() => setLoaded(true));
    return () => { listeners.delete(onUpdate); };
  }, []);

  return { features, loaded };
}
